<template>
  <div class="orderAsideBidNew">
    <div class="top p-16 md:p-24">
      <div class="flex border-b border-grey-light pb-16">
        <div class="hotel-image" v-lazy:background-image="this['order/order'].hotel.coverImage || require('@/assets/images/default-room.jpg')">
        </div>
        <div class="ml-16 py-4">
          <div class="text-base font-medium">{{ this['order/order'].hotel.hotelName }}</div>
          <div class="text-sm text-grey-darker mt-8">{{ this['order/order'].hotel.address }}</div>
        </div>
      </div>
      <div class="order-date py-16 text-sm border-b border-grey-light">
        <div class="font-medium">{{ $t('date') }}</div>
        <div class="flex mt-8" style="gap: 8px">
          <div class="font-medium">{{ $t('checkIn.time') }}</div>
          <div>{{ $options.filters.toDate(this['order/order'].startAt, 'LL ddd') }}</div>
          <div>
            <span>{{ this['order/order'].hotel.check.checkInStartTimeAt }}</span>
            <span> ~ </span>
            <span>{{ this['order/order'].hotel.check.checkInEndTimeAt }}</span>
            <el-tooltip v-if="this['order/order'].hotel.check.isAllowEarlyCheckIn" effect="dark" placement="bottom">
              <i class="text-grey-dark owl-status-circle-info"></i>
              <div slot="content" class="leading-tight" style="max-width: 320px;">
                <span>{{ $t('checkIn.earlyFee') }}</span>
                <span v-if="isNumber(allowEarlyCheckInPricePerHour)">{{ currencySign }} {{ allowEarlyCheckInPricePerHour | currencyConvert({inputCurrency: this['order/order'].hotel.currency}) }}</span>
                <span v-else>{{ allowEarlyCheckInPricePerHour }}</span>
                <span v-if="isNumber(allowEarlyCheckInPricePerHour)">/{{ $t('unit.perHour') }}</span>
              </div>
            </el-tooltip>
          </div>
        </div>
        <div class="flex mt-8" style="gap: 8px">
          <div class="font-medium">{{ $t('checkOut.time') }}</div>
          <div>{{ $options.filters.toDate(this['order/order'].endAt, 'LL ddd') }}</div>
          <div>
            <span>{{ this['order/order'].hotel.check.checkOutTimeAt }}</span>
            <el-tooltip v-if="this['order/order'].hotel.check.isAllowLateCheckOut" effect="dark" placement="bottom">
              <i class="text-grey-dark owl-status-circle-info"></i>
              <div slot="content" class="leading-tight" style="max-width: 320px;">
                <span>{{ $t('checkOut.latelyFee') }}</span>
                <span v-if="isNumber(allowLateCheckOutPricePerHour)">{{ currencySign }} {{ allowLateCheckOutPricePerHour | currencyConvert({inputCurrency: this['order/order'].hotel.currency}) }}</span>
                <span v-else>{{ allowLateCheckOutPricePerHour }}</span>
                <span v-if="isNumber(allowLateCheckOutPricePerHour)">/{{ $t('unit.perHour') }}</span>
              </div>
            </el-tooltip>
          </div>
        </div>
      </div>
      <div class="py-16 text-sm border-b border-grey-light">
        <div class="font-medium">{{ $t('peopleLimit') }}</div>
        <div class="mt-8">
          <span>{{ $t('adultCount', { count: this['order/order'].adultCount }) }}, {{ $t('childCount', { count: this['order/order'].childAges.length }) }}</span>
        </div>
        <div v-if="isMoreThanAdultLimit && isMoreThanAdultLimit > 0" class="flex items-center justify-between bg-grey-lightest mt-8 py-8 px-16" style="border-radius: 20px">
          <div class="flex items-center">
            <i class="text-base owl-status-triangle-warning text-red mr-4" />
            <div class="text-xs text-red font-medium">
              {{ this.$t('peopleLimitWarning.needToPlan', {num: isMoreThanAdultLimit}) }}
            </div>
          </div>
          <router-link :to="goToHotel('123')" class="text-xs font-bold text-red underline cursor-pointer">
            {{ $t('reselect') }}
          </router-link>
        </div>
      </div>
      <div class="py-16 text-sm border-b border-grey-light">
        <div class="font-medium">{{ $t('priceInclude') }}</div>
        <div class="tooltip-block-top relative text-right w-full text-sm font-medium">
          <p v-if="bidNumber" :class="['tooltip block xl:hidden', tooltipType]">{{ tooltipText }}</p>
          <span :class="bidNumber > 0 && 'lineThough'">{{ currencySign }} {{ unTaxRoomTotal | currencyConvert({inputCurrency: hotelCurrency, listInteger: true}) }}</span>
        </div>
        <div class="tooltip-block relative flex justify-between items-center mt-8 text-sm">
          <p v-if="bidNumber" :class="['tooltip hidden xl:block', tooltipType]">{{ tooltipText }}</p>
          <div class="font-medium">{{ $t('totalPrice') }}</div>
          <div class="flex flex-col items-center ml-16 font-bold">
            <div class="flex items-center">
              <span class="text-sm mr-4">
                {{ this['user/currencySign'] }}
              </span>
              <div class="relative bidNumber-block">
                <input
                  class="bidNumber text-sm"
                  type="number"
                  v-model="bidNumber"
                  style="max-width: 195px"
                  :placeholder="$t('bid.form.bidPricePlaceholder')"
                >
              </div>
            </div>
            <div class="errorMsg text-right w-full">{{ validation.firstError('bidNumber') }}</div>
          </div>
        </div>
        <div class="flex justify-between items-center mt-16 text-sm">
          <div class="flex items-center">
            <div class="font-medium">{{ $t('taxesAndServiceFee') }}</div>
            <el-tooltip effect="dark" placement="bottom">
              <i class="text-grey-dark owl-status-circle-info"></i>
              <div slot="content" class="leading-tight" style="max-width: 320px;">
                {{ $t('taxesDescription') }}
              </div>
            </el-tooltip>
          </div>
          <div>
            <span v-if="isCurrencyConvert" class="text-sm">{{ $t('priceAbout') }} </span>
            {{ currencySign }} {{ taxTotal | currencyConvert({ inputCurrency: hotelCurrency, listInteger: true }) }}
          </div>
        </div>
      </div>
      <div class="py-16 text-base">
        <div class="flex justify-between items-center">
          <span class="font-semibold leading-normal">{{ $t('orderTotalToPay') }}</span>
          <div class="aside__payment-count text-left sm:text-right">
            <div class="font-medium">
              <span v-if="isCurrencyConvert">{{ $t('priceAbout') }} </span>{{ currencySign }} {{ (trialBidTotal || orderTotalPrice) | currencyConvert({inputCurrency: hotelCurrency, isCeil: true, listInteger: true}) }} <small class='text-xs'>({{ $t('includeTax') }})</small>
            </div>
          </div>
        </div>
        <div>
          <template v-if="isCurrencyConvert">
            <div class="mt-4 text-grey-dark text-sm font-bold text-left sm:text-right">({{ hotelCurrency | currencySign }} {{ orderTotalPrice | toCurrency }})</div>
            <div class="text-xs mt-8 flex items-center bg-grey-lightest px-16 py-8" style="border-radius: 20px;">
              <i class="text-orange owl-status-triangle-warning text-base"></i>
              {{ $t('displayedPriceWarning') }}
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="bottom p-16 md:p-24 mt-24">
      <div class="flex items-center justify-between text-lg">
        <div class="font-bold">{{ $t('bidToNormal.title') }}</div>
        <img src="@/assets/images/ticket.svg" alt="">
      </div>
      <div class="mt-8 text-sm leading-normal">
        {{ $t('bidToNormal.desp') }}
        <span class="text-blue-light cursor-pointer font-medium" @click="onStartOrderingBid">{{ $t('bidToNormal.go') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import SimpleVueValidator from 'simple-vue-validator'
import { mapActions, mapGetters } from 'vuex'
import { createHelpers } from 'vuex-map-fields'
import animateScrollTo from 'animated-scroll-to'

const { mapFields } = createHelpers({
  getterType: 'order/getField',
  mutationType: 'order/updateField'
})

const Validator = SimpleVueValidator.Validator

export default {
  name: 'OrderAsideBidNew',
  props: {
    abtest: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      includeTip: null,
      excludeTip: null,
      trialBidTotal: '',
      separatePrice: '',
      isCreatingOrder: false
    }
  },
  validators: {
    'bidNumber': function (val) {
      return Validator.value(val).required().lessThan(this.currencyRefTrans)
    }
  },
  filters: {
    toPercentage: function (val) {
      return 100 - val * 100
    }
  },
  computed: {
    ...mapFields({
      bidNumber: 'form.bidNumber'
    }),
    ...mapGetters([
      'isMobile',
      'order/sortedCart',
      'order/order',
      'search/dateRange',
      'user/currencySign',
      'user/selectedCurrency',
      'order/isDiscount',
      'order/discountTotal',
      'order/selectedCoupon',
      'order/premiumCoupon'
    ]),
    isAddBedDisplay: function () {
      let count = 0
      for (const room of this['order/order'].rooms) {
        for (const plan of room.plans) {
          for (const type in plan.bed) {
            count += plan.bed[type]
          }
        }
      }
      return count > 0
    },
    bedTotal: function () {
      let count = 0
      if (this['order/order'].hotel.extraBedRules) {
        let price = {
          adult: this['order/order'].hotel.extraBedRules.adult.isCharge ? this['order/order'].hotel.extraBedRules.adult.price : 0,
          child: this['order/order'].hotel.extraBedRules.child.isCharge ? this['order/order'].hotel.extraBedRules.child.price : 0,
          infant: 0
        }
        for (const room of this['order/order'].rooms) {
          for (const plan of room.plans) {
            for (const type in plan.bed) {
              count += plan.bed[type] * price[type] * this.totalNights
            }
          }
        }
      }
      return count
    },
    peopleTotal: function () {
      return {
        total: [this['order/order'].adultCount, this['order/order'].childAges.length].reduce((x, y) => Number(x) + Number(y)),
        child: Number(this['order/order'].childAges.length) || 0
      }
    },
    addPeopleCount: function () {
      const array = _.flatten(this['order/order'].rooms)
      const plans = _.flatten(array.reduce((acc, curr) => {
        acc.push(curr.plans)
        return acc
      }, []))
      return _.sumBy(plans, function(plan) {
        return Number(plan.bed?.adult)
      })
    },
    isMoreThanAdultLimit: function () {
      const rooms = _.flatten(this['order/order'].rooms)
      const plans = _.flatten(rooms.reduce((acc, curr) => {
        curr.plans.forEach(e => {
          acc.push({ ...e, maxPeople: curr.maxPeople })
        })
        return acc
      }, []))
      const limit = plans.reduce((acc, curr) => {
        return acc + (curr.maxPeople * curr.count)
      }, 0)
      if ((this.peopleTotal.total - this.peopleTotal.child) > limit) {
        return (this.peopleTotal.total - this.peopleTotal.child) - limit - this.addPeopleCount
      } else {
        return false
      }
    },
    allPlans: function () {
      const array = _.flatten(this['order/order'].rooms)
      return _.flatten(array.reduce((acc, curr) => {
        acc.push(curr.plans)
        return acc
      }, []))
    },
    isMultipleCancelPolicy: function () {
      const result = _.groupBy(this.allPlans, item => {
        return item.cancelPolicy.title
      })
      return result && _.size(result) > 1
    },
    isCurrencyConvert: function () {
      return this['order/order'].currency !== this['user/selectedCurrency']
    },
    hotelCurrency: function () {
      return this['order/order'].currency
    },
    currencySign: function () {
      return this['user/currencySign']
    },
    totalNights: function () {
      const start = this.$dayjs(this['order/order'].startAt)
      const end = this.$dayjs(this['order/order'].endAt)
      return end.diff(start, 'days')
    },
    taxTotal: function () {
      return Number(this['order/order'].vat) + Number(this['order/order'].fee)
    },
    unTaxRoomTotal: function () {
      return Number(this['order/order'].untaxedPrice) + Number(this.bedTotal) + Number(this['order/order'].extraAddonPrice)
    },
    orderTotalPrice: function () {
      return Number(this['order/order'].total)
    },
    tooltipType: function () {
      if (!this.bidNumber) {
        return ''
      }
      if (this.bidNumber >= this.currencyBidTrans && this.bidNumber < this.currencyRefTrans) {
        return 'success'
      }
      if (this.bidNumber < this.currencyBidTrans) {
        return 'low'
      }
      if (this.bidNumber >= this.currencyRefTrans) {
        return 'noWay'
      }
      return ''
    },
    tooltipText: function () {
      if (!this.bidNumber) {
        return ''
      }
      if (this.bidNumber >= this.currencyBidTrans && this.bidNumber < this.currencyRefTrans) {
        return this.abtest === 'A' ? this.$t('bid.form.rate_A.may_success') : this.$t('bid.form.rate_B.may_success')
      }
      if (this.bidNumber < this.currencyBidTrans) {
        return this.abtest === 'A' ? this.$t('bid.form.rate_A.low') : this.$t('bid.form.rate_B.low')
      }
      if (this.bidNumber >= this.currencyRefTrans) {
        return this.$t('bid.form.rate_A.error')
      }
      return ''
    },
    currencyBidTrans: function () {
      return this.checkIfString(this.$options.filters.currencyConvert(this.separatePrice, { inputCurrency: this.hotelCurrency, isCeil: true, listInteger: true }))
    },
    currencyRefTrans: function () {
      return this.checkIfString(this.$options.filters.currencyConvert(this.unTaxRoomTotal, { inputCurrency: this.hotelCurrency, outputCurrency: this['user/selectedCurrency'], isCeil: true, listInteger: true }))
    },
    addonTotal: function () {
      return Number(this['order/order'].extraAddonPrice)
    },
    allowEarlyCheckInPricePerHour: function () {
      if (Number(this['order/order'].hotel.check.allowEarlyCheckInPricePerHour) > 0) {
        return this['order/order'].hotel.check.allowEarlyCheckInPricePerHour
      } else {
        // return this.$t('free')
        return 0
      }
    },
    allowLateCheckOutPricePerHour: function () {
      if (Number(this['order/order'].hotel.check.allowLateCheckOutPricePerHour) > 0) {
        return this['order/order'].hotel.check.allowLateCheckOutPricePerHour
      } else {
        // return this.$t('free')
        return 0
      }
    }
  },
  methods: {
    ...mapActions([
      'order/onDeleteCartItem',
      'order/getBidding',
      'order/onSubmitOrderToBid'
    ]),
    onStartOrderingBid: function () {
      window.dataLayer.push({
        event: 'order_to-general-order'
      })

      if (this.isCreatingOrder) {
        return false
      }
      this.startOrderingBid(false)
    },
    startOrderingBid: function (bid) {
      this.isCreatingOrder = true
      this['order/onSubmitOrderToBid']({
        bid: bid,
        order: this['order/order']
      }).then(res => {
        // this.safeOpenWindow(routeData.href, '_blank')
        if (!this.isMobile) {
          const routeData = this.$router.resolve({ name: 'Callback', query: { orderSerial: res.orderSerial, orderToken: res.orderToken } })
          window.open(routeData.href, '_blank')
        } else {
          this.$router.push({
            name: 'Callback',
            query: {
              orderSerial: res.orderSerial,
              orderToken: res.orderToken
            }
          })
        }
      })
        .catch(err => {
          const keepRoomsResponseCodes = [11036, 80002, 81008, 81001, 81002] // 特定的 response status 不須顯示重新選擇日期的提示

          if (!keepRoomsResponseCodes.includes(err)) {
            setTimeout(() => {
            // this['hotels/SET_ROOMS']([])
              this.$modal.hide('dialog')
            }, 3000)
          }
        }).finally(() => {
          this.isCreatingOrder = false
        })
    },
    trialBid () {
      this['order/getBidding']({
        orderSerial: this.$route.query.orderSerial,
        bidPrice: this.bidNumber || '',
        currency: this['user/selectedCurrency']
      })
        .then(res => {
          if (res.status === 200) {
            this.trialBidTotal = res.data.trialOrderPrice

            if (!this.separatePrice) {
              this.separatePrice = +res.data.referencePrice
            }
          }
          if (res.status === 404) {
            this.$router.replace({ name: 'Home' })
          }
        })
    },
    handleChange () {
      this.trialBid()
    },
    checkIfString (num) {
      if (typeof num === 'string') {
        if (num.indexOf(',') !== -1) {
          // 有被format過
          return +num.replace(',', '')
        } else {
          return +num
        }
      } else {
        return +num
      }
    },
    onBidNumberValidate: function () {
      return new Promise((resolve, reject) => {
        this.$validate().then(success => {
          if (success) {
            resolve()
          } else {
            const errorForms = document.getElementsByClassName('errorMsg')
            let errorElement = {}
            for (const el of errorForms) {
              if (el.innerText !== '') {
                errorElement = el
                break
              }
            }

            if (!_.isEmpty(errorElement)) {
              animateScrollTo(errorElement, {
                offset: -200,
                speed: 800
              })
            }
            let errorString = {}
            for (const error of this.validation.errors) {
              errorString[error.field] = {
                message: error.message
              }
            }
            reject(new Error('form error: ' + errorString.join(',')))
          }
        })
      })
    },
    goToHotel: function () {
      return {
        name: 'HotelDetail',
        params: { hotelId: this['order/order'].hotel.hotelUUID },
        query: {
          start: this['order/order'].startAt,
          end: this['order/order'].endAt,
          adult: this['order/order'].adultCount,
          child: this['order/order'].childAges?.length ? this['order/order'].childAges?.length : null,
          ages: this['order/order'].childAges?.length ? this['order/order'].childAges?.toString() : null,
          externalId: this['order/order'].hotel.hotelUUID,
          searchId: this['order/order'].hotel.hotelUUID,
          search: this['order/order'].hotel.hotelName,
          searchType: 'hotel',
          source: this['order/order'].hotel.source
        }
      }
    },
    isNumber: function (value) {
      return !isNaN(value)
    }
  },
  created () {
    this.trialBid()
  },
  beforeDestroy () {
    this.bidNumber = ''
  },
  watch: {
    bidNumber: {
      handler: _.debounce(async function (val) {
        this.handleChange()
      }, 500)
    }
  }
}
</script>

<style lang="sass" scoped>
.orderAsideBidNew
  // @apply shadow
  // @apply border-1 border-grey-lighter
  @apply sticky
  top: 100px
  // border-radius: 20px
  .lineThough
    // text-decoration: line-through #D23A07 1.5px
    text-decoration-line: line-through
    text-decoration-color: #D23A07
    text-decoration-thickness: 1.5px
  .top
    @apply border-1 border-grey-lighter
    border-radius: 20px
    .hotel-image
      // padding-top: imageRatio(80, 0)
      @apply bg-center bg-cover relative overflow-hidden
      border-radius: 20px
      width: 80px
      height: 80px
      background-color: #f7f7f7
  .bottom
    @apply bg-blue-lightest border-1 border-solid border-blue-light
    border-radius: 20px
  .tooltip-block-top
    .tooltip
      @apply absolute text-white rounded-full text-center cursor-default bg-black text-sm px-8 py-4 leading-tight
      top: -35px
      right: 0
      // transform: translateX(50%)
      &:after
        content: ""
        position: absolute
        top: 100%
        right: 15px
        margin-left: -8px
        border-width: 7px
        border-style: solid
      &.low
        @apply bg-orange
        &:after
          border-color: #F7AC15 transparent transparent transparent
      &.success
        background-color: #C4A56E
        &:after
          border-color: #C4A56E transparent transparent transparent
      &.noWay
        background-color: #EA5E2E
        &:after
          border-color: #EA5E2E transparent transparent transparent
  .bidNumber-block, .tooltip-block
    .tooltip
      @apply absolute text-white rounded-full text-center cursor-default bg-black text-sm px-8 py-4 leading-tight
      top: -35px
      right: 24%
      // transform: translateX(50%)
      &:after
        content: ""
        position: absolute
        top: 100%
        right: 15px
        margin-left: -8px
        border-width: 7px
        border-style: solid
      &.low
        @apply bg-orange
        &:after
          border-color: #F7AC15 transparent transparent transparent
      &.success
        background-color: #C4A56E
        &:after
          border-color: #C4A56E transparent transparent transparent
      &.noWay
        background-color: #EA5E2E
        &:after
          border-color: #EA5E2E transparent transparent transparent
    .bidNumber
      @apply relative
      @apply bg-grey-lightest
      @apply rounded-full
      @apply py-8 px-16 flex-auto
      line-height: 1 // IME 輸入時顯示底線
      max-width: 100%
      &::-webkit-outer-spin-button, &::-webkit-inner-spin-button
        -webkit-appearance: none
        margin: 0
      &[type=number]
      -moz-appearance: textfield
      @screen md
        max-width: 175px
.aside__block
  &--sub
    @apply border-t-1 border-grey-lightest
    // @apply px-24 py-16
    @apply flex flex-col
  &--item-row
    @apply text-sm text-grey-darker leading-md flex flex-col
    @screen sm
      @apply flex-row justify-between
    &:not(:last-of-type)
      @apply mb-16
      @screen sm
        @apply mb-0
    .discount-display
      @apply max-w-full
      @screen sm
        max-width: 50%
.aside__info
  @apply mt-16
.aside__title
  @apply text-sm text-white
.aside__context
  @apply mt-8 text-white
  @apply flex justify-start items-center
  i
    @apply ml-8 text-grey-darker
  &--favor
    @apply text-red
  &--tax
    @apply text-grey-dark

.aside__payment
  // @apply flex justify-between items-start
.aside__payment-count
  @apply text-xl
  // @apply text-blue-light

.aside__agreement
  label
    @apply items-start

.aside__hotel
  @apply flex justify-between items-start
.aside__hotel-count
  @apply text-grey-dark
.tippy
  @apply text-grey-dark text-sm mb-8
  i
    @apply text-grey-dark text-sm
.errorMsg
  @apply text-xs text-red ml-4 mt-8
</style>
